import React, { useEffect, useState } from "react";
import { CRMLayout } from "@/components/CRMLayout";
import { api } from "@/lib/api";
import { Sparkles, Plus, Send, Mail, MessageCircle, Smartphone, X, CheckCircle2, ChevronRight } from "lucide-react";

const CHANNELS = [
  { v: "email", l: "Email", Icon: Mail },
  { v: "sms", l: "SMS", Icon: Smartphone },
  { v: "whatsapp", l: "WhatsApp", Icon: MessageCircle },
];

const TONES = [
  { v: "luxury", l: "Luxury" },
  { v: "warm", l: "Warm" },
  { v: "direct", l: "Direct" },
  { v: "formal", l: "Formal" },
];

const GOALS = [
  { v: "intro", l: "Intro" },
  { v: "follow_up", l: "Follow-up" },
  { v: "nurture", l: "Nurture" },
  { v: "book_meeting", l: "Book meeting" },
];

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : "—";

const Outreach = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/ai/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <CRMLayout>
      <div className="flex items-end justify-between mb-6" data-testid="outreach-page">
        <div>
          <div className="flex items-center gap-2 overline text-gold"><Sparkles className="w-3 h-3" /> AI Outreach</div>
          <h1 className="font-display text-3xl text-navy mt-2">Campaigns</h1>
          <p className="text-sm text-slate-500 mt-1">AI writes personalised emails, SMS, and WhatsApp messages for your selected leads. Review, edit, send.</p>
        </div>
        <button onClick={() => setOpen(true)} className="btn-navy text-sm py-2.5 px-4" data-testid="campaign-new-btn"><Plus className="w-4 h-4" /> New Campaign</button>
      </div>

      <div className="bg-white border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Channel</th>
              <th className="text-left p-3">Leads</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Created</th>
              <th className="text-left p-3 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={c.id} onClick={() => setDetail(c.id)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`campaign-row-${c.id}`}>
                <td className="p-3 font-semibold text-navy">{c.name}</td>
                <td className="p-3 uppercase text-xs tracking-widest">{c.channel}</td>
                <td className="p-3">{(c.lead_ids || []).length}</td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-0.5 uppercase tracking-wider font-semibold ${
                    c.status === "sent" ? "bg-emerald-100 text-emerald-800" :
                    c.status === "ready" ? "bg-blue-100 text-blue-800" :
                    c.status === "drafting" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"
                  }`}>{c.status}</span>
                </td>
                <td className="p-3 text-slate-500 text-xs">{fmtDate(c.created_at)}</td>
                <td className="p-3 text-navy"><ChevronRight className="w-4 h-4" /></td>
              </tr>
            ))}
            {campaigns.length === 0 && <tr><td colSpan="6" className="p-10 text-center text-slate-500">No campaigns yet — create your first AI outreach campaign.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && <CampaignModal onClose={() => setOpen(false)} onCreated={load} />}
      {detail && <CampaignDetail id={detail} onClose={() => { setDetail(null); load(); }} />}
    </CRMLayout>
  );
};

const CampaignModal = ({ onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [leads, setLeads] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [form, setForm] = useState({ name: "", channel: "email", tone: "luxury", goal: "intro" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/crm/leads").then((r) => setLeads(r.data)).catch(() => {});
  }, []);

  const togglePick = (id) => {
    const n = new Set(picked);
    n.has(id) ? n.delete(id) : n.add(id);
    setPicked(n);
  };

  const submit = async () => {
    if (picked.size === 0 || !form.name) return;
    setBusy(true);
    try {
      await api.post("/ai/campaigns", { ...form, lead_ids: Array.from(picked) });
      onCreated();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-navy/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between">
          <div>
            <div className="overline text-gold">Step {step} of 2</div>
            <h2 className="font-display text-2xl text-navy mt-1">{step === 1 ? "Select leads" : "Configure & generate"}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-navy" data-testid="campaign-modal-close"><X className="w-5 h-5" /></button>
        </div>

        {step === 1 && (
          <div className="p-8" data-testid="campaign-step-1">
            <div className="text-sm text-slate-500 mb-4">{picked.size} selected</div>
            <div className="max-h-[400px] overflow-y-auto border border-slate-200 divide-y divide-slate-100">
              {leads.map((l) => (
                <label key={l.id} className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer" data-testid={`campaign-lead-${l.id}`}>
                  <input type="checkbox" checked={picked.has(l.id)} onChange={() => togglePick(l.id)} />
                  <div className="flex-1">
                    <div className="font-semibold text-navy text-sm">{l.name}</div>
                    <div className="text-xs text-slate-400">{l.email}{l.company ? ` · ${l.company}` : ""} · {l.category || l.interest}</div>
                  </div>
                </label>
              ))}
              {leads.length === 0 && <div className="p-6 text-center text-slate-500 text-sm">No leads yet — add some from Lead Search or manually.</div>}
            </div>
            <div className="flex justify-end mt-5">
              <button onClick={() => setStep(2)} disabled={picked.size === 0} className="btn-navy text-sm py-2.5 px-5 disabled:opacity-50" data-testid="campaign-next-btn">Next →</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="p-8 space-y-5" data-testid="campaign-step-2">
            <input required placeholder="Campaign name (e.g. Q2 Austin Dental Outreach)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-slate-200 px-4 py-3 text-sm" data-testid="campaign-name" />

            <div>
              <div className="overline text-gold mb-2">Channel</div>
              <div className="grid grid-cols-3 gap-3">
                {CHANNELS.map(({ v, l, Icon }) => (
                  <button key={v} onClick={() => setForm({ ...form, channel: v })} className={`p-4 border flex flex-col items-center gap-2 transition ${form.channel === v ? "border-gold bg-slate-50" : "border-slate-200 hover:border-navy"}`} data-testid={`campaign-channel-${v}`}>
                    <Icon className="w-5 h-5 text-gold" strokeWidth={1.5} />
                    <div className="text-sm font-semibold text-navy">{l}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="overline text-gold mb-2">Tone</div>
                <div className="flex gap-2 flex-wrap">
                  {TONES.map((t) => (
                    <button key={t.v} onClick={() => setForm({ ...form, tone: t.v })} className={`text-xs px-3 py-1.5 border ${form.tone === t.v ? "border-gold bg-slate-50 text-navy font-semibold" : "border-slate-200 text-slate-600"}`} data-testid={`campaign-tone-${t.v}`}>{t.l}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="overline text-gold mb-2">Goal</div>
                <div className="flex gap-2 flex-wrap">
                  {GOALS.map((g) => (
                    <button key={g.v} onClick={() => setForm({ ...form, goal: g.v })} className={`text-xs px-3 py-1.5 border ${form.goal === g.v ? "border-gold bg-slate-50 text-navy font-semibold" : "border-slate-200 text-slate-600"}`} data-testid={`campaign-goal-${g.v}`}>{g.l}</button>
                  ))}
                </div>
              </div>
            </div>

            <div className="text-xs text-slate-500 bg-slate-50 p-3 border-l-4 border-gold">
              AI will generate a personalised message for each of the {picked.size} selected leads. You'll review and can edit before sending.
            </div>

            <div className="flex justify-between gap-3 pt-4 border-t border-slate-200">
              <button onClick={() => setStep(1)} className="text-sm text-slate-500 px-4 py-2">← Back</button>
              <button onClick={submit} disabled={busy || !form.name} className="btn-navy text-sm py-2.5 px-6 disabled:opacity-50" data-testid="campaign-generate-btn">
                <Sparkles className="w-4 h-4" /> {busy ? "Generating…" : "Generate Messages"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CampaignDetail = ({ id, onClose }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edited, setEdited] = useState({});

  const load = () => api.get(`/ai/campaigns/${id}`).then((r) => setData(r.data)).catch(() => onClose());
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const saveEdit = async (m) => {
    const body = edited[m.id];
    if (!body) return;
    // Messages are stored per-campaign; PATCH isn't built. For V1 keep client-side only.
    setEdited((prev) => { const n = { ...prev }; delete n[m.id]; return n; });
  };

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/ai/campaigns/${id}/send`, {});
      alert(`${r.data.sent_mock} messages queued (MOCKED — configure Resend/Twilio to transmit).`);
      load();
    } finally { setBusy(false); }
  };

  if (!data) return null;
  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-navy/70" />
      <div className="w-full max-w-3xl bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="campaign-detail">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-start justify-between z-10">
          <div>
            <div className="overline text-gold flex items-center gap-2"><Sparkles className="w-3 h-3" /> {data.channel} campaign</div>
            <h2 className="font-display text-2xl text-navy mt-1">{data.name}</h2>
            <div className="text-xs text-slate-500 mt-1">
              {(data.messages || []).length} messages · {data.tone} · goal: {data.goal}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {data.status !== "sent" ? (
              <button onClick={send} disabled={busy} className="btn-gold text-sm py-2 px-4 disabled:opacity-50" data-testid="campaign-send-btn">
                <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send (mock)"}
              </button>
            ) : (
              <span className="text-xs px-3 py-1.5 bg-emerald-100 text-emerald-800 font-semibold uppercase tracking-widest inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> Sent
              </span>
            )}
            <button onClick={onClose} className="text-slate-400"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {data.status !== "sent" && (
          <div className="px-6 py-3 bg-amber-50 border-b border-amber-200 text-xs text-amber-900">
            <strong>Mocked send.</strong> Messages will be logged to each lead's timeline but not actually transmitted. Add Resend API key (email) or Twilio (SMS/WhatsApp) to enable real sending.
          </div>
        )}

        <div className="p-6 space-y-4">
          {(data.messages || []).map((m) => (
            <div key={m.id} className="bg-white border border-slate-200 p-5" data-testid={`campaign-msg-${m.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-semibold text-navy">{m.lead_name}</div>
                  <div className="text-xs text-slate-400">{m.lead_email}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 uppercase tracking-widest font-semibold ${
                  m.status === "sent_mock" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"
                }`}>{m.status}</span>
              </div>
              {m.subject && (
                <div className="mb-2">
                  <div className="overline text-gold text-[10px]">Subject</div>
                  <div className="text-sm font-semibold text-navy mt-0.5">{m.subject}</div>
                </div>
              )}
              {edited[m.id] !== undefined ? (
                <>
                  <textarea value={edited[m.id]} onChange={(e) => setEdited({ ...edited, [m.id]: e.target.value })} rows={6} className="w-full border border-slate-200 px-3 py-2 text-sm" />
                  <div className="flex justify-end gap-2 mt-2">
                    <button onClick={() => setEdited((p) => { const n = { ...p }; delete n[m.id]; return n; })} className="text-xs text-slate-500">Cancel</button>
                    <button onClick={() => saveEdit(m)} className="text-xs text-navy font-semibold">Save</button>
                  </div>
                </>
              ) : (
                <>
                  <pre className="text-sm text-navy whitespace-pre-wrap font-sans leading-relaxed">{m.body}</pre>
                  {m.cta && <div className="mt-3 text-xs text-gold font-semibold">CTA: {m.cta}</div>}
                  {data.status !== "sent" && (
                    <button onClick={() => setEdited({ ...edited, [m.id]: m.body })} className="mt-3 text-xs text-slate-500 hover:text-navy" data-testid={`msg-edit-${m.id}`}>Edit locally</button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Outreach;
